/**
 * Settings shell: a grouped navigation rail on the left and the active settings
 * panel on the right. The rail is theme-aware, grouped into sections, and
 * collapses to a horizontal scroller on small screens.
 * @component
 * @returns {JSX.Element}
 */
import React, { useState } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PeopleIcon from '@mui/icons-material/People';
import TuneIcon from '@mui/icons-material/Tune';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  AssignmentTurnedInOutlined,
  ChecklistOutlined,
  LayersOutlined,
  ShieldOutlined,
} from '@mui/icons-material';
import WorkplaceSettings from './WorkplaceSettings';
import NotificationsSettings from './NotificationsSettings';
import UsersSettings from './UsersSettings';
import GuestAccessSettings from './GuestAccessSettings';
import CheckListTemplateSettings from './CheckListTemplateSettings';
import ActionItemTemplateSettings from './ActionItemTemplateSettings';
import PreferencesSettings from './PreferencesSettings';
import EpicsSettings from './EpicsSettings';
import AssistantMemorySettings from './AssistantMemorySettings';
import useCurrentUser from '../../utils/useCurrentUser';

const NAV_SECTIONS = [
  {
    heading: 'Templates',
    items: [
      {
        key: 'aitemplate',
        label: 'Task Templates',
        description: 'Reusable action-item templates',
        icon: <AssignmentTurnedInOutlined />,
        Component: ActionItemTemplateSettings,
      },
      {
        key: 'checklist',
        label: 'Checklist Templates',
        description: 'Standardise repeatable checklists',
        icon: <ChecklistOutlined />,
        Component: CheckListTemplateSettings,
      },
    ],
  },
  {
    heading: 'Organisation',
    items: [
      {
        key: 'workplace',
        label: 'Workspaces',
        description: 'Manage workspaces & access',
        icon: <BusinessIcon />,
        Component: WorkplaceSettings,
      },
      {
        key: 'users',
        label: 'Users',
        description: 'Roles & account access',
        icon: <PeopleIcon />,
        Component: UsersSettings,
      },
      {
        key: 'epics',
        label: 'Epics',
        description: 'Review & delete epics',
        icon: <LayersOutlined />,
        Component: EpicsSettings,
        adminOnly: true,
      },
      {
        key: 'guest-access',
        label: 'Guest Access',
        description: 'What guests can see & do',
        icon: <ShieldOutlined />,
        Component: GuestAccessSettings,
        adminOnly: true,
      },
    ],
  },
  {
    heading: 'Personal',
    items: [
      {
        key: 'notifications',
        label: 'Notifications',
        description: 'Email & in-app alerts',
        icon: <NotificationsIcon />,
        Component: NotificationsSettings,
      },
      {
        key: 'preferences',
        label: 'Preferences',
        description: 'Display & interaction options',
        icon: <TuneIcon />,
        Component: PreferencesSettings,
      },
      {
        key: 'assistant-memory',
        label: 'Assistant Memory',
        description: 'What the AI knows about you',
        icon: <PsychologyOutlinedIcon />,
        Component: AssistantMemorySettings,
      },
    ],
  },
];

const SettingView = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selected, setSelected] = useState('aitemplate');
  const { isAdmin } = useCurrentUser();

  // Hide admin-only surfaces (e.g. Guest Access) from non-admins.
  const sections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => !item.adminOnly || isAdmin),
  })).filter((section) => section.items.length > 0);

  const allItems = sections.flatMap((s) => s.items);
  const active = allItems.find((i) => i.key === selected) || allItems[0];
  const ActiveComponent = active.Component;

  const navItem = (item) => (
    <ListItemButton
      key={item.key}
      selected={selected === item.key}
      onClick={() => setSelected(item.key)}
      sx={{
        borderRadius: 2,
        mb: 0.5,
        minWidth: isMobile ? 200 : 'auto',
        '&.Mui-selected': {
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          '&:hover': { bgcolor: 'primary.dark' },
          '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
        {item.icon}
      </ListItemIcon>
      <ListItemText
        primary={item.label}
        secondary={isMobile ? null : item.description}
        primaryTypographyProps={{ fontWeight: 600, variant: 'body2' }}
        secondaryTypographyProps={{
          variant: 'caption',
          sx: {
            color: selected === item.key ? 'primary.contrastText' : 'text.disabled',
            opacity: selected === item.key ? 0.85 : 1,
          },
        }}
      />
    </ListItemButton>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        height: '100vh',
        bgcolor: 'background.default',
      }}
    >
      {/* Navigation rail */}
      <Paper
        elevation={0}
        square
        sx={{
          width: { xs: '100%', md: 280 },
          flexShrink: 0,
          borderRight: { md: '1px solid' },
          borderBottom: { xs: '1px solid', md: 'none' },
          borderColor: { xs: 'divider', md: 'divider' },
          display: 'flex',
          flexDirection: 'column',
          overflowY: { md: 'auto' },
        }}
      >
        <Box sx={{ px: 3, pt: 3, pb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <SettingsIcon color="primary" />
          <Typography variant="h6" fontWeight={700}>
            Settings
          </Typography>
        </Box>

        <Box
          sx={{
            px: 1.5,
            pb: 2,
            display: 'flex',
            flexDirection: { xs: 'row', md: 'column' },
            overflowX: { xs: 'auto', md: 'visible' },
            gap: { xs: 2, md: 0 },
          }}
        >
          {sections.map((section) => (
            <Box key={section.heading} sx={{ minWidth: { xs: 'max-content', md: 'auto' } }}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ px: 2, mt: { md: 1 }, display: 'block' }}
              >
                {section.heading}
              </Typography>
              <List
                disablePadding
                sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'column' } }}
              >
                {section.items.map(navItem)}
              </List>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Content area */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, sm: 3, md: 4 } }}>
          <ActiveComponent />
        </Box>
      </Box>
    </Box>
  );
};

export default SettingView;
